import { useState, useEffect, useRef } from 'react'
import { startPhCheckout, startGlobalCheckout } from './lib/checkout.js'
import { useRegion } from './useRegion.js'

/// Lightweight standalone thock. Preloads the same NovelKeys Cream alpha
/// samples App.jsx uses on the landing logo. Web Audio is overkill for a
/// single button on a checkout page, so we use HTMLAudioElement directly.
function useThock() {
  const down = useRef(null)
  const up = useRef(null)
  useEffect(() => {
    const d = new Audio('/thock-down.wav'); d.volume = 0.55; d.preload = 'auto'
    const u = new Audio('/thock-up.wav');   u.volume = 0.40; u.preload = 'auto'
    down.current = d; up.current = u
  }, [])
  return {
    playDown: () => { const a = down.current; if (a) { try { a.currentTime = 0; a.play().catch(() => {}) } catch {} } },
    playUp:   () => { const a = up.current;   if (a) { try { a.currentTime = 0; a.play().catch(() => {}) } catch {} } },
  }
}

export default function BuyPage() {
  const { isPH: detectedPH, loading: regionLoading } = useRegion()
  const thock = useThock()
  // ?force=ph is a dev-only escape hatch for testing the PH flow on any
  // machine. In production, region must come from Vercel's edge header so
  // non-PH visitors can't self-route to the cheaper tier.
  // ?force=global is allowed in every env. Overriding yourself UP to the
  // higher-priced tier is not an attack, and PH visitors who want to pay in
  // USD (gift card, expat workflow) might legitimately want this.
  const [forced] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    const v = params.get('force')
    if (v === 'global') return 'global'
    if (v === 'ph' && import.meta.env.DEV) return 'ph'
    return null
  })
  const isPH = forced === 'ph' || (forced !== 'global' && detectedPH)
  const [status, setStatus] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('status') || 'idle'
  })
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [typoSuggestion, setTypoSuggestion] = useState(null)

  // Same format regex as the backend. The backend additionally does a DNS
  // MX lookup which we don't replicate here (would block the UI thread on
  // a network call); we just surface the server-returned error if the
  // domain turns out to be bogus.
  const EMAIL_FORMAT = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/
  const emailValid = EMAIL_FORMAT.test(email.trim()) && email.trim().length <= 254

  useEffect(() => {
    const onPop = () => {
      const params = new URLSearchParams(window.location.search)
      setStatus(params.get('status') || 'idle')
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  // Kicks off a background download of the MSI without navigating the
  // current tab. /download/* is served with Content-Disposition: attachment
  // (vercel.json), so the browser drops the bytes straight into the user's
  // downloads tray. Safe to call multiple times — browsers dedupe identical
  // ongoing fetches.
  function startWindowsDownload() {
    try {
      const link = document.createElement('a')
      link.href = '/download/KeebySetup.msi'
      link.rel = 'noopener'
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch {
      // Silent: download is a nice-to-have. The success view + email both
      // include explicit download CTAs as fallbacks.
    }
  }

  async function onBuy() {
    if (isPH && !emailValid) {
      setError('Please enter a valid email so we can send your license key.')
      return
    }
    setLoading(true)
    setError(null)
    setTypoSuggestion(null)
    // Start the download as soon as the buyer commits. By the time they
    // come back from PayMongo or Polar checkout, the installer is already
    // sitting in their downloads, ready to install + activate.
    startWindowsDownload()
    try {
      if (isPH) await startPhCheckout({ email: email.trim().toLowerCase() })
      else startGlobalCheckout()
    } catch (e) {
      if (e.code === 'email_typo' && e.suggestion) {
        setTypoSuggestion(e.suggestion)
        setError(null)
      } else {
        setError(e.message || 'Something went wrong')
      }
      setLoading(false)
    }
  }

  function applyTypoSuggestion() {
    const at = email.indexOf('@')
    if (at < 0 || !typoSuggestion) return
    const corrected = email.slice(0, at + 1) + typoSuggestion
    setEmail(corrected)
    setTypoSuggestion(null)
    setError(null)
  }

  if (status === 'success') return <SuccessView isPH={isPH} />
  if (status === 'cancelled') return <CancelledView onRetry={() => { setStatus('idle'); window.history.replaceState(null, '', '/windows') }} />

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-16">
    <article className="w-full max-w-[420px]">
      <div className="mb-10 text-center">
        <button
          type="button"
          onPointerDown={thock.playDown}
          onPointerUp={thock.playUp}
          onPointerCancel={thock.playUp}
          data-keeby-thock
          aria-label="thock"
          className="mx-auto mb-6 block rounded-[16px] focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 cursor-pointer active:scale-95 transition-transform"
        >
          <img src="/keeby-logo.webp" alt="Keeby" className="h-16 w-16 rounded-[16px] pointer-events-none select-none" />
        </button>
        <h1 className="text-3xl font-extrabold tracking-[-1.5px] leading-tight text-neutral-900 mb-2 md:text-4xl">
          Get Keeby for Windows
        </h1>
        <p className="text-[15px] text-neutral-600">Lifetime license, activates on up to 2 devices.</p>
      </div>

      <div className="rounded-2xl border border-black/[0.04] bg-white p-6 md:p-8">
        <div className="flex items-start justify-between mb-6">
          <span className="text-[15px] text-neutral-600 leading-[40px]">One-time</span>
          <div className="flex flex-col items-end">
            <PriceTag isPH={isPH} regionLoading={regionLoading} />
            {/* Only shown for global. PH users see no extra line so the price
                row stays compact; loading state also omits it, so there's no
                shift between loading -> PH (the majority path). Global users
                see a tiny one-time shift when region resolves, acceptable
                since we never know they're global until the fetch returns. */}
            {!regionLoading && !isPH && (
              <span className="text-[12px] text-neutral-600 mt-0.5">
                + taxes at checkout
              </span>
            )}
          </div>
        </div>

        {!regionLoading && isPH && (
          // Email is collected here, before redirecting to PayMongo, because
          // QRPh and some GCash flows skip PayMongo's own email step. Without
          // this we'd take payment and have no way to deliver the key.
          <div className="mb-4">
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (error) setError(null); if (typoSuggestion) setTypoSuggestion(null) }}
              className="w-full rounded-xl border border-black/[0.08] bg-white px-4 py-3 text-[15px] tracking-tight text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-neutral-400"
            />
            {typoSuggestion ? (
              <p className="mt-2 text-[12px] text-amber-700 tracking-tight">
                Did you mean{' '}
                <button
                  type="button"
                  onClick={applyTypoSuggestion}
                  className="font-semibold underline cursor-pointer"
                >
                  @{typoSuggestion}
                </button>
                ? Click to fix.
              </p>
            ) : (
              <p className="mt-2 text-[12px] text-neutral-600 tracking-tight">We'll send your license key here.</p>
            )}
          </div>
        )}

        <button
          onClick={onBuy}
          disabled={loading || regionLoading || (isPH && !emailValid)}
          className="w-full rounded-full bg-gradient-to-b from-[#2a2a2a] to-[#0a0a0a] border border-white/[0.08] px-6 py-4 text-[15px] font-semibold text-white disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
        >
          {regionLoading
            ? 'Loading…'
            : loading
              ? 'Opening checkout…'
              : isPH
                ? 'Pay with GCash, Maya, or QRPh'
                : 'Buy with card'}
        </button>

        {error && (
          <p className="mt-3 text-[13px] text-red-600 text-center">{error}</p>
        )}

        <p className="mt-6 text-[12px] text-neutral-600 text-center leading-relaxed">
          Your license key will be emailed to you right after payment.
          {isPH ? ' PH e-wallets only, no cards.' : ''}
        </p>
      </div>

      <div className="mt-8 text-center">
        <a
          href="/download/KeebySetup.msi"
          className="inline-flex items-center gap-2 rounded-full border border-black/[0.08] bg-white px-5 py-2.5 text-[13px] font-semibold text-neutral-700 tracking-tight hover:bg-neutral-50 cursor-pointer"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Download Keeby for Windows
        </a>
        <p className="mt-2 text-[12px] text-neutral-600 tracking-tight">
          Free to download. Activates with the license key you'll get after paying.
        </p>
      </div>

    </article>
    </div>
  )
}

function PriceTag({ isPH, regionLoading }) {
  // Reserve a consistent box so the rendered price (₱199 or $5.59) and the
  // loading skeleton occupy the same width and height. Without this the
  // baseline shifts left/right when region detection resolves.
  const outer = "inline-flex items-baseline justify-end min-w-[90px] h-10"

  if (regionLoading) {
    return (
      <span className={outer}>
        <span className="h-7 w-16 self-center rounded-md bg-neutral-200 animate-pulse" />
      </span>
    )
  }
  if (isPH) {
    return (
      <span className={outer}>
        <span className="text-4xl font-extrabold tracking-tight text-neutral-900">₱199</span>
      </span>
    )
  }
  return (
    <span className={`${outer} gap-1`}>
      <span className="text-2xl font-extrabold tracking-tight text-neutral-400">$</span>
      <span className="text-4xl font-extrabold tracking-tight text-neutral-900">4.99</span>
    </span>
  )
}

function SuccessView({ isPH }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-16">
    <article className="w-full max-w-[420px] text-center">
      <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
        <svg className="h-8 w-8 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h1 className="text-3xl font-extrabold tracking-[-1.5px] leading-tight text-neutral-900 mb-3 md:text-4xl">
        Thanks for buying Keeby
      </h1>
      <p className="text-[15px] text-neutral-600 mb-8 leading-relaxed">
        We've sent your license key to your email. Download Keeby below, then paste the key on first launch to activate.
      </p>
      <a
        href="/download/KeebySetup.msi"
        className="inline-block rounded-full bg-gradient-to-b from-[#2a2a2a] to-[#0a0a0a] border border-white/[0.08] px-8 py-3 text-[14px] font-semibold text-white tracking-tight cursor-pointer mb-6"
      >
        Download Keeby for Windows
      </a>
      <p className="text-[13px] text-neutral-600 tracking-tight">
        Don't see the email? Check your spam folder, or email <a href="mailto:hello@getkeeby.com" className="text-neutral-600 underline">hello@getkeeby.com</a>.
      </p>
    </article>
    </div>
  )
}

function CancelledView({ onRetry }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-16">
    <article className="w-full max-w-[420px] text-center">
      <h1 className="text-3xl font-extrabold tracking-[-1.5px] leading-tight text-neutral-900 mb-3 md:text-4xl">
        Checkout cancelled
      </h1>
      <p className="text-[15px] text-neutral-600 mb-8">No payment was taken.</p>
      <button
        onClick={onRetry}
        className="rounded-full bg-gradient-to-b from-[#2a2a2a] to-[#0a0a0a] border border-white/[0.08] px-8 py-3 text-[14px] font-semibold text-white cursor-pointer"
      >
        Try again
      </button>
    </article>
    </div>
  )
}
